import React, { useState } from 'react';
import {
  Box, Container, Grid, IconButton, Skeleton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ZicassoChat,
  BackNavigationAppBar,
  OutlinedIcons,
} from '@marageti/z4-lib';
import {
  Conversation,
  Message,
  GetParticipantsResponse,
  TripMatch,
  Partner,
} from '@marageti/z4-sdk/lib/travel';
import { PersonType } from '@marageti/z4-sdk/lib/people';
import AddDocumentsDialog from '../../../../components/add-documents-dialog';
import MobileAlertDialog from '../../../../components/legacy-trip-request-dialog';
import Sidebar from '../../sidebar';

type Props = {
  conversationTitle?: string,
  conversation: Conversation | undefined,
  handleViewClick: (message: Message) => void,
  isError: boolean,
  isLoading: boolean,
  isValidating: boolean,
  messages: Message[] | undefined,
  lastMessageSeenId: string | undefined,
  loadMoreMessages: () => void,
  hasMoreMessages: boolean,
  participants: GetParticipantsResponse | undefined,
  sendMessage: (message: string) => void,
  viewer: {
    id: string | undefined,
    type: PersonType,
  },
  updateMessages: () => void,
  tripMatch: TripMatch | undefined,
  partner?: Partner | undefined,
  isLegacyTR?: boolean,
};

const MessagesView = ({
  conversationTitle = undefined,
  conversation,
  handleViewClick,
  isError,
  isLoading,
  isValidating,
  messages,
  lastMessageSeenId,
  loadMoreMessages,
  hasMoreMessages,
  participants,
  sendMessage,
  viewer,
  updateMessages,
  tripMatch,
  partner,
  isLegacyTR = false,
}: Props) => {
  const [showAddDocuments, setShowAddDocuments] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('desktop'));

  const [dialogOpen, setDialogOpen] = useState(false);

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const {
    OutlinedInformationCircleIcon,
  } = OutlinedIcons;

  if (isMobile) {
    return (
      <>
        <Container
          sx={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'primary.contrastText',
            zIndex: 1200,
            padding: 0,
          }}
        >
          {isLegacyTR && <MobileAlertDialog open={dialogOpen} onClose={handleDialogClose} />}
          <BackNavigationAppBar
            primary={conversationTitle || <Skeleton width={150} />}
            defaultPath={`/trip-requests/${tripMatch?.tripRequestId}`}
            options={{
              replace: true,
            }}
            endAdornment={isLegacyTR ? (
              <IconButton onClick={handleDialogOpen}>
                <OutlinedInformationCircleIcon color="primary" />
              </IconButton>
            ) : null}
          />

          <Container
            maxWidth="xl"
            sx={{
              display: 'flex',
              flex: 1,
              p: 0,
              minHeight: 'calc(100vh - 72px)',
            }}
          >
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              width: '100%',
              height: 'calc(100dvh - 72px)',
              px: 3,
            }}
            >
              <ZicassoChat
                isError={isError}
                isLoading={isLoading}
                isValidating={isValidating}
                conversation={conversation}
                messages={messages}
                lastMessageSeenId={lastMessageSeenId}
                loadPreviousPages={hasMoreMessages}
                onLoadPreviousPage={loadMoreMessages}
                onSend={sendMessage}
                onViewClick={handleViewClick}
                participants={participants}
                viewer={viewer}
                contextMenuItems={[
                  {
                    icon: <OutlinedIcons.OutlinedUploadIcon color="primary" />,
                    action: () => setShowAddDocuments(true),
                    label: 'Upload Document',
                  },
                ]}
              />
            </Box>
          </Container>
        </Container>
        <AddDocumentsDialog
          closeDialog={() => setShowAddDocuments(false)}
          open={showAddDocuments}
          onDocumentUploaded={updateMessages}
        />
      </>
    );
  }
  return (
    <>
      <Container
        maxWidth="xl"
        sx={{
          display: 'flex',
          flex: 1,
          pt: 3.75,
        }}
      >
        <Grid
          container
          spacing={1}
        >
          <Grid
            item
            desktop={4}
            sx={{
              display: 'block',
              borderRight: '1px solid #C2C2C2',
            }}
          >
            {/* Sticky Sidebar */}
            <Sidebar isMessagingPage partner={partner} isLegacyTR={isLegacyTR} />
          </Grid>
          <Grid
            item
            desktop={8}
          >
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              width: '100%',
              height: 'calc(100vh - 198px)',
            }}
            >
              <ZicassoChat
                isError={isError}
                isLoading={isLoading}
                isValidating={isValidating}
                conversation={conversation}
                messages={messages}
                lastMessageSeenId={lastMessageSeenId}
                loadPreviousPages={hasMoreMessages}
                onLoadPreviousPage={loadMoreMessages}
                onSend={sendMessage}
                onViewClick={handleViewClick}
                participants={participants}
                viewer={viewer}
                contextMenuItems={[
                  {
                    icon: <OutlinedIcons.OutlinedUploadIcon color="primary" />,
                    action: () => setShowAddDocuments(true),
                    label: 'Upload Document',
                  },
                ]}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
      <AddDocumentsDialog
        closeDialog={() => setShowAddDocuments(false)}
        open={showAddDocuments}
        onDocumentUploaded={updateMessages}
      />
    </>
  );
};

export default MessagesView;
